import type { MembershipSummary, OrganizationSummary } from "@/types/provisionUser";
import { ORGANIZATION_ROLES, type OrganizationRole, type UserPermission } from "@/types/user";

/**
 * Pure rules for which organization a signed-in user lands in and what role
 * they hold there. Kept free of React and Firebase so AuthContext and the
 * header switcher share one tested definition.
 */

/** The organization currently in use, with the caller's role and permission in it. */
export interface ActiveOrganization {
  organization: OrganizationSummary;
  role: OrganizationRole | null;
  permission: UserPermission | null;
}

export interface OrganizationSwitcherOption {
  organization: OrganizationSummary;
  role: OrganizationRole | null;
  /** True when only super-admin access (no membership) puts this org in the list. */
  superAdminOnly: boolean;
}

/**
 * Higher is more privileged. Unknown roles (iOS has an `owner` role the web
 * list lacks), null and undefined rank -1 rather than throwing.
 */
export function roleRank(role: string | null | undefined): number {
  return ORGANIZATION_ROLES.indexOf(role as OrganizationRole);
}

export function toOrganizationSummary(m: MembershipSummary): OrganizationSummary {
  return { id: m.orgId, name: m.orgName, email: m.orgEmail, tosType: m.orgTosType };
}

/** An org with an empty name has not finished provisioning; any named org outranks it. */
const isComplete = (m: MembershipSummary): boolean => m.orgName !== "";

function compareMemberships(a: MembershipSummary, b: MembershipSummary): number {
  return (
    Number(isComplete(b)) - Number(isComplete(a)) ||
    roleRank(b.role) - roleRank(a.role) ||
    a.orgName.localeCompare(b.orgName) ||
    a.orgId.localeCompare(b.orgId)
  );
}

/**
 * The org with id `orgId` if the user can reach it: one of their memberships,
 * or (super admins only) any org in `allOrganizations`. Returns the object from
 * those arrays itself, never a copy, so callers holding it keep a stable
 * reference; null for a missing id or an unreachable org.
 */
export function findReachableOrganization(
  orgId: string | undefined | null,
  {
    organizations,
    allOrganizations,
    isSuperAdmin,
  }: {
    organizations: OrganizationSummary[] | undefined;
    allOrganizations?: OrganizationSummary[] | undefined;
    isSuperAdmin: boolean;
  }
): OrganizationSummary | null {
  if (!orgId) {
    return null;
  }
  const hasId = (o: OrganizationSummary) => o.id === orgId;
  return organizations?.find(hasId) ?? (isSuperAdmin ? allOrganizations?.find(hasId) : undefined) ?? null;
}

/**
 * The organization to select on sign-in: the preferred org (the one the URL
 * names) if the user can reach it, then the last-used org likewise, otherwise
 * their best membership, otherwise (super admins only) the first org of the
 * caller's name-sorted `allOrganizations`.
 */
export function pickDefaultOrganization({
  memberships,
  allOrganizations,
  isSuperAdmin,
  lastUsedOrgId,
  preferredOrgId,
}: {
  memberships: MembershipSummary[];
  allOrganizations?: OrganizationSummary[];
  isSuperAdmin: boolean;
  lastUsedOrgId: string | null;
  preferredOrgId?: string | null;
}): OrganizationSummary | null {
  const reachableMemberships = memberships.filter((m) => m.orgId);
  const superAdminOrganizations = isSuperAdmin ? (allOrganizations ?? []).filter((o) => o.id) : [];
  const candidates = { organizations: reachableMemberships.map(toOrganizationSummary), allOrganizations, isSuperAdmin };

  const preferredOrLastUsed =
    findReachableOrganization(preferredOrgId, candidates) ?? findReachableOrganization(lastUsedOrgId, candidates);
  if (preferredOrLastUsed) {
    return preferredOrLastUsed;
  }

  if (reachableMemberships.length > 0) {
    return toOrganizationSummary([...reachableMemberships].sort(compareMemberships)[0]);
  }

  return superAdminOrganizations[0] ?? null;
}

export function resolveActiveOrganization(
  org: OrganizationSummary,
  memberships: MembershipSummary[] | undefined,
  isSuperAdmin: boolean
): ActiveOrganization {
  const membership = memberships?.find((m) => m.orgId === org.id);
  if (membership) {
    return {
      organization: org,
      role: membership.role,
      permission: { role: membership.role, tosAcceptedVersion: membership.tosAcceptedVersion } as UserPermission,
    };
  }
  if (isSuperAdmin) {
    return { organization: org, role: "admin", permission: null };
  }
  return { organization: org, role: null, permission: null };
}

/** The header switcher's entries: every org for a super admin, otherwise the user's memberships. Name-sorted. */
export function organizationSwitcherOptions({
  memberships,
  allOrganizations,
  isSuperAdmin,
}: {
  memberships: MembershipSummary[] | undefined;
  allOrganizations: OrganizationSummary[] | undefined;
  isSuperAdmin: boolean;
}): OrganizationSwitcherOption[] {
  const options: OrganizationSwitcherOption[] = isSuperAdmin
    ? (allOrganizations ?? []).map((organization) => {
        const membership = memberships?.find((m) => m.orgId === organization.id);
        return { organization, role: membership?.role ?? null, superAdminOnly: !membership };
      })
    : (memberships ?? []).map((m) => ({ organization: toOrganizationSummary(m), role: m.role, superAdminOnly: false }));

  return options
    .filter((option) => option.organization.id)
    .sort((a, b) => a.organization.name.localeCompare(b.organization.name));
}

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** The access badge shown next to an org in the picker and the old-link chooser. */
export function organizationAccessLabel({ role, superAdminOnly }: OrganizationSwitcherOption): string | null {
  if (superAdminOnly) {
    return "Super admin";
  }
  return role ? capitalize(role) : null;
}
